import apiClient, { API_BASE_URL, ApiError } from '../core';

/**
 * Represents a media item in the Media Library
 */
export interface MediaItem {
  id: string;
  userId?: string;
  siteId?: string;
  url: string;
  filename: string;
  size: number;
  mimeType: string;
  s3Key?: string;
  cloudfrontUrl?: string;
  componentType?: string;
  assetType?: string;
  optimized?: boolean;
  createdAt: string;
}

export const uploadApi = {
  async uploadFile(file: File, siteId?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (siteId) {
      formData.append('siteId', siteId);
    }

    // Let axios set the Content-Type automatically for FormData
    const response = await apiClient.post('/upload', formData);

    return response.data;
  },

  // Upload navbar logo with component-specific optimizations
  async uploadNavbarLogo(
    file: File,
    siteId: string,
    options?: {
      alt?: string;
      maxWidth?: number;
      maxHeight?: number;
      quality?: number;
    }
  ): Promise<{
    id: string;
    url: string;
    cloudfrontUrl?: string;
    filename: string;
    alt: string;
    mimeType: string;
    size: number;
    optimized: boolean;
    mediaId: string;
    s3Key?: string;
    createdAt: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('siteId', siteId);

    // Add optional parameters exactly as the backend expects them
    if (options?.alt) {
      formData.append('alt', options.alt);
    }
    if (options?.maxWidth) {
      formData.append('maxWidth', options.maxWidth.toString());
    }
    if (options?.maxHeight) {
      formData.append('maxHeight', options.maxHeight.toString());
    }
    if (options?.quality) {
      formData.append('quality', options.quality.toString());
    }

    console.log('FormData contents:');
    for (const [key, value] of formData.entries()) {
      console.log(`- ${key}:`, value);
    }

    // Use fetch directly to avoid axios interceptor issues with FormData
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new ApiError('Authentication required', 401);
    }

    const response = await fetch(`${API_BASE_URL}/upload/navbar-logo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type - let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorData);
      } catch {
        errorObj = { error: errorData || 'Upload failed' };
      }
      throw new ApiError(errorObj.error || 'Upload failed', response.status, errorObj);
    }

    const data = await response.json();
    return {
      id: data.id,
      url: data.url,
      cloudfrontUrl: data.cloudfrontUrl,
      filename: data.filename,
      alt: data.alt,
      mimeType: data.mimeType,
      size: data.size,
      optimized: data.optimized,
      mediaId: data.mediaId,
      s3Key: data.s3Key,
      createdAt: data.createdAt,
    };
  },

  // Get navbar logos for a specific site
  async getNavbarLogos(siteId: string): Promise<{
    siteId: string;
    logos: Array<{
      id: string;
      url: string;
      cloudfrontUrl?: string;
      filename: string;
      alt: string;
      mimeType: string;
      size: number;
      optimized: boolean;
      mediaId: string;
      s3Key?: string;
      createdAt: string;
      isVector: boolean;
      isPNG: boolean;
      hasTransparency: boolean;
    }>;
    total: number;
    recommendations: {
      preferredFormat: string;
      maxDimensions: string;
      maxFileSize: string;
    };
  }> {
    const response = await apiClient.get(`/upload/navbar-logos/${siteId}`);
    return response.data;
  },

  // Upload component-specific file
  async uploadComponentFile(
    file: File,
    siteId: string,
    componentType: string,
    assetType: string,
    options?: {
      optimize?: boolean;
      maxWidth?: number;
      maxHeight?: number;
      quality?: number;
    }
  ): Promise<{
    id: string;
    url: string;
    filename: string;
    size: number;
    mimeType: string;
    s3Key?: string;
    cloudfrontUrl?: string;
    componentType: string;
    assetType: string;
    optimized: boolean;
    createdAt: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('siteId', siteId);
    formData.append('componentType', componentType);
    formData.append('assetType', assetType);

    // Add optional optimization parameters
    if (options?.optimize !== undefined) formData.append('optimize', options.optimize.toString());
    if (options?.maxWidth) formData.append('maxWidth', options.maxWidth.toString());
    if (options?.maxHeight) formData.append('maxHeight', options.maxHeight.toString());
    if (options?.quality) formData.append('quality', options.quality.toString());

    console.log('FormData contents for component upload:');
    for (const [key, value] of formData.entries()) {
      console.log(`- ${key}:`, value);
    }

    // Use fetch directly to avoid axios interceptor issues with FormData
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new ApiError('Authentication required', 401);
    }

    const response = await fetch(`${API_BASE_URL}/upload/component`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type - let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorData);
      } catch {
        errorObj = { error: errorData || 'Upload failed' };
      }
      throw new ApiError(errorObj.error || 'Upload failed', response.status, errorObj);
    }

    return await response.json();
  },

  async getMedia(siteId?: string): Promise<MediaItem[]> {
    const params = siteId ? `?siteId=${siteId}` : '';
    const response = await apiClient.get<MediaItem[]>(`/upload/media${params}`);
    return response.data;
  },

  async deleteMedia(mediaId: string): Promise<void> {
    await apiClient.delete(`/upload/media/${mediaId}`);
  },

  // Get component-specific media
  async getComponentMedia(
    componentType: string,
    assetType: string,
    siteId?: string
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);

    const response = await apiClient.get(
      `/upload/media/component/${componentType}/${assetType}?${params}`
    );
    return response.data;
  },

  // Get upload configuration
  async getUploadConfig(): Promise<{
    maxFileSize: string;
    s3Configured: boolean;
    supportedFormats: Record<string, Record<string, string[]>>;
  }> {
    const response = await apiClient.get('/upload/config');
    return response.data;
  },

  // Upload team member image
  async uploadTeamMemberImage(
    file: File,
    siteId: string,
    memberId: string,
    memberName?: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      url: string;
      cloudfrontUrl?: string;
      filename: string;
      mimeType: string;
      size: number;
      optimized: boolean;
      memberId: string;
      memberName?: string;
      componentType: string;
      assetType: string;
      createdAt: string;
    };
  }> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('siteId', siteId);
    formData.append('memberId', memberId);
    if (memberName) {
      formData.append('memberName', memberName);
    }

    // Use fetch directly like we did for navbar logos
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new ApiError('Authentication required', 401);
    }

    const response = await fetch(`${API_BASE_URL}/upload/team-member`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorData);
      } catch {
        errorObj = { error: errorData || 'Upload failed' };
      }
      throw new ApiError(errorObj.error || 'Upload failed', response.status, errorObj);
    }

    return await response.json();
  },

  // Get team member images for a site
  async getTeamMemberImages(siteId: string): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      url: string;
      cloudfrontUrl?: string;
      filename: string;
      mimeType: string;
      size: number;
      componentType: string;
      assetType: string;
      createdAt: string;
    }>;
  }> {
    const response = await apiClient.get(`/upload/team-members/${siteId}`);
    return response.data;
  },

  // Get presigned URL for direct S3 upload (supports unlimited file sizes)
  async getPresignedUploadUrl(params: {
    filename: string;
    contentType: string;
    fileSize: number;
    siteId?: string;
    componentType?: string;
    assetType?: string;
  }): Promise<{
    uploadUrl: string;
    key: string;
    publicUrl: string;
    cloudfrontUrl?: string;
    expiresIn: number;
  }> {
    const response = await apiClient.post('/upload/presigned-url', params);
    return response.data;
  },

  // Confirm direct S3 upload and save metadata
  async confirmUpload(params: {
    key: string;
    filename: string;
    contentType: string;
    fileSize: number;
    publicUrl: string;
    cloudfrontUrl?: string;
    siteId?: string;
    componentType?: string;
    assetType?: string;
  }): Promise<{
    id: string;
    url: string;
    filename: string;
    size: number;
    mimeType: string;
    s3Key?: string;
    cloudfrontUrl?: string;
    componentType?: string;
    assetType?: string;
    optimized: boolean;
    createdAt: string;
  }> {
    const response = await apiClient.post('/upload/confirm', params);
    return response.data;
  },

  // Upload file directly to S3 using presigned URL with progress tracking
  async uploadDirectToS3(
    file: File,
    uploadUrl: string,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  },

  // Full presigned upload flow: get URL, upload to S3, confirm
  async uploadWithPresignedUrl(
    file: File,
    options: {
      siteId?: string;
      componentType?: string;
      assetType?: string;
      onProgress?: (progress: number) => void;
    }
  ): Promise<{
    id: string;
    url: string;
    filename: string;
    size: number;
    mimeType: string;
    s3Key?: string;
    cloudfrontUrl?: string;
    componentType?: string;
    assetType?: string;
    optimized: boolean;
    createdAt: string;
  }> {
    // Step 1: Get presigned URL
    const presignedData = await this.getPresignedUploadUrl({
      filename: file.name,
      contentType: file.type,
      fileSize: file.size,
      siteId: options.siteId,
      componentType: options.componentType,
      assetType: options.assetType,
    });

    // Step 2: Upload directly to S3
    await this.uploadDirectToS3(file, presignedData.uploadUrl, file.type, options.onProgress);

    // Step 3: Confirm upload and save metadata
    const result = await this.confirmUpload({
      key: presignedData.key,
      filename: file.name,
      contentType: file.type,
      fileSize: file.size,
      publicUrl: presignedData.publicUrl,
      cloudfrontUrl: presignedData.cloudfrontUrl,
      siteId: options.siteId,
      componentType: options.componentType,
      assetType: options.assetType,
    });

    return result;
  },
};
